import { useActionSheet } from "@expo/react-native-action-sheet";
import { manipulateAsync } from "expo-image-manipulator";
import {
  type ImagePickerResult,
  MediaTypeOptions,
  launchCameraAsync,
  launchImageLibraryAsync,
  requestCameraPermissionsAsync,
  requestMediaLibraryPermissionsAsync,
} from "expo-image-picker";
import React, { useCallback, useMemo } from "react";
import { Alert, Linking, StyleSheet } from "react-native";

import { isWeb } from "@stepapp/core";

import { palette, spacing } from "../styles";
import Block from "./Block";
import SvgIcon from "./SvgIcon";
import Touchable from "./Touchable";

type Props = {
  onAfterPhotoSelect?: () => void;
  onBeforePhotoSelect?: () => void;
  onPhotoSelect: (base64: string) => void;
  children?: NotWorth;
  t: NotWorth;
};

const ImagePicker = ({
  children,
  onBeforePhotoSelect,
  onPhotoSelect,
  onAfterPhotoSelect,
  t,
}: Props) => {
  const { showActionSheetWithOptions } = useActionSheet();

  const showPermissionDeniedAlert = useCallback(() => {
    Alert.alert(
      t("notifications.permissionDenied.camera"),
      t("notifications.permissionDenied.cameraDescription"),
      [
        { text: t("common.OK"), style: "cancel" },
        { text: t("screens.settings.title"), onPress: () => Linking.openSettings() },
      ],
    );
  }, [t]);

  const launchOptions = useMemo(
    () => ({
      allowsEditing: true,
      allowsMultipleSelection: false,
      mediaTypes: MediaTypeOptions.Images,
      base64: true,
      quality: 0.1,
    }),
    [],
  );

  const handlePhotoSelect = useCallback(
    async (image?: ImagePickerResult | null) => {
      const imageUri = image?.assets?.[0]?.uri;

      if (!imageUri) return;

      onBeforePhotoSelect?.();

      const { base64 } = await manipulateAsync(imageUri, [], { compress: 0.3, base64: true });

      onAfterPhotoSelect?.();

      if (!base64) return;

      onPhotoSelect(base64);
    },
    [onPhotoSelect, onBeforePhotoSelect, onAfterPhotoSelect],
  );

  const openPicker = useCallback(async () => {
    const { granted } = await requestMediaLibraryPermissionsAsync();
    if (!granted) showPermissionDeniedAlert();

    return launchImageLibraryAsync(launchOptions);
  }, [launchOptions, showPermissionDeniedAlert]);

  const openCamera = useCallback(async () => {
    const { granted } = await requestCameraPermissionsAsync();
    if (!granted) showPermissionDeniedAlert();

    return launchCameraAsync(launchOptions);
  }, [launchOptions, showPermissionDeniedAlert]);

  const handlePickerAction = useCallback(async () => {
    if (isWeb) {
      const image = await openPicker();
      handlePhotoSelect(image);
    } else {
      showActionSheetWithOptions(
        {
          title: "",
          options: [t("common.choosePhoto"), t("common.takePhoto"), t("common.cancel")],
          cancelButtonIndex: 2,
        },
        async (index) => {
          const image = index === 0 ? await openPicker() : index === 1 ? await openCamera() : null;
          handlePhotoSelect(image);
        },
      );
    }
  }, [handlePhotoSelect, openCamera, openPicker, showActionSheetWithOptions, t]);

  return children ? (
    React.cloneElement(children, { onPress: handlePickerAction })
  ) : (
    <Touchable onPress={handlePickerAction} style={styles.buttonContainer}>
      <Block flex={1}>
        <SvgIcon color={palette.common.white} name="camera" size={24} />
      </Block>
    </Touchable>
  );
};

export default ImagePicker;

const styles = StyleSheet.create({
  buttonContainer: {
    alignItems: "center",
    backgroundColor: palette.primary.accent,
    borderRadius: spacing.unit * 4,
    padding: spacing.unit * 1.75,
  },
});
